import React, { useState, useEffect } from 'react';
import useStyles from './styles';
import { get, size, set, setWith } from 'global/lib/lodash';
import PermissionGate from 'global/components/permission-gate';
import { Button, Checkbox, Icon, Typography, FormGroup, FormControlLabel } from '@material-ui/core';
import Styles, { Loader } from 'global/styles';
import Droprealms, { Dropitems } from 'global/components/droprealms';
import { getUserAppPermissions, reverseUserAppPermissions, cleanCopy } from 'global/utils';
import clsx from 'clsx';
import Confirm from 'global/components/confirm';
import sendData from 'redux/actions/sendData';
import { useVendors, useClients } from 'global/hooks/useVendors';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { uppercaseFirst } from 'global/utils';

const sortRealmNames = (_a, _b, me) => {
  const userRealmId = get(me, 'realmId');
  let keyA = 'client';
  let keyB = 'client';
  if (userRealmId === get(_a, keyA + '.id')) {
    keyA = 'vendor';
  }
  if (userRealmId === get(_b, keyB + '.id')) {
    keyB = 'vendor';
  }
  const a = get(_a, keyA + '.name', '').toLowerCase();
  const b = get(_b, keyB + '.name', '').toLowerCase();
  // if letters are equal
  return a === b ? 0 : a > b ? 1 : -1;
};

export const VendorList = (props) => {
  const { data, title, nameType } = props;
  const [isEdit, setIsEdit] = useState(false);
  const [state, setState] = useState({});
  const [confirmModal, setConfirmModal] = useState();
  const [loading, setLoading] = useState(props.loading);
  const { filterPermissionsByNav } = useNavSettings();
  const { me } = useUser();
  const myRealmId = get(me, 'realm.id');
  const [selItems, setSelItems] = useState({});

  const classes = useStyles();
  const styles = Styles();

  const canEdit = get(props, 'canEdit', true);
  const hideBorders = get(props, 'hideBorders', false);

  useEffect(() => {
    const s = {};
    if (isEdit) {
      data.map((realm) => {
        const userPerms = getUserAppPermissions(realm.permissions);
        (userPerms || []).map((perm) => {
          set(s, getSelector(realm, perm), true);
        });
      });
    }
    setState(s);
  }, [isEdit]);

  useEffect(() => {
    setLoading(props.loading);
    if (!props.loading) {
      const items = cleanCopy(selItems);
      (data || []).map((d) => {
        const { editions, scorecards, id } = d;
        editions.map((e) => setWith(items, [id, 'editions', `${e.id}`], true, Object));
        scorecards.map((e) => setWith(items, [id, 'scorecards', `${e.id}`], true, Object));
      });
      setSelItems(items);
    }
  }, [props.loading]);

  const toggleEdit = () => {
    setIsEdit(!isEdit);
    if (isEdit) {
      props.update();
    }
  };

  const itemToUse = get(props, 'nameType', 'client');

  const onDelete = (realm) => {
    setConfirmModal({
      title: `Delete ${uppercaseFirst(itemToUse)}?`,
      content: `Are you sure you want to delete ${get(realm, [itemToUse, 'name'])}?`,
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        setLoading(true);
        setConfirmModal();

        await sendData(
          {
            url: `/client-vendors/${realm.id}`,
            method: 'patch',
            data: {
              data: {
                type: 'client-vendors',
                id: realm.id,
                attributes: {
                  archived: true,
                },
              },
            },
          },
          { simulate: false }
        );

        props.update();
      },
    });
  };

  const getSelector = (realm, perm) => {
    return `${realm.id}.${perm.value}`;
  };

  const updateVendor = async (realm, _state) => {
    try {
      const statePerms = get(_state, realm.id);
      const permissions = reverseUserAppPermissions(statePerms);
      const result = await sendData(
        {
          url: `/client-vendors/${realm.id}`,
          method: 'patch',
          data: {
            data: {
              type: 'client-vendors',
              id: realm.id,
              attributes: {
                permissions: permissions.map((p) => ({ name: `${p.match}.${myRealmId}` })),
              },
            },
          },
        },
        { simulate: false, normalize: { build: ['clientVendors'] } }
      );
      return result;
    } catch (err) {
      console.warn(err.message);
      alert(`Failed to update vendor: ${err.message}`);
    }
  };

  const updateRealms = async (realm, selRealms) => {
    try {
      setLoading(true);
      await sendData(
        {
          url: `/client-vendors/${realm.id}`,
          method: 'patch',
          data: {
            data: {
              type: 'client-vendors',
              id: realm.id,
              relationships: {
                realms: {
                  data: Object.keys(selRealms).map((k) => ({ type: 'realms', id: get(selRealms, [k, 'id']) })),
                },
              },
            },
          },
        },
        { simulate: false, normalize: { build: ['clientVendors'] } }
      );
      await props.update();
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const checkChange = async (realm, perm) => {
    const s = { ...state };
    const selector = getSelector(realm, perm);
    const checked = get(s, selector);
    set(s, selector, !checked);
    setState(s);

    updateVendor(realm, s);
  };

  const getRealmName = (realm) => {
    const realmname = get(realm, [itemToUse, 'name']);
    if (props.namePrefix) {
      return `${props.namePrefix}${realmname}`;
    } else {
      return realmname;
    }
  };

  const handleChange = (r, e, type = 'editions') => {
    const key = [r.id, type, e.id];
    set(selItems, key, !get(selItems, key));
    setSelItems({ ...selItems });
  };

  const resetSel = (r, type) => {
    Object.keys(get(selItems, [r.id, type], {})).map((e) => set(selItems, [r.id, type, e], true));
    setSelItems({ ...selItems });
  };

  const updateItems = async (realm, type = 'editions') => {
    try {
      setLoading(true);
      const key = `${realm.id}.${type}`;
      const items = Object.keys(get(selItems, key, {})).map((k) => ({ id: k, value: get(selItems, `${key}.${k}`) }));
      await sendData({
        url: `/client-vendors/${realm.id}`,
        method: 'patch',
        data: {
          data: {
            type: 'client-vendors',
            id: realm.id,
            relationships: {
              [type]: {
                data: items.filter((e) => !!e.value).map((e) => ({ type, id: e.id })),
              },
            },
          },
        },
      });
      await props.update();
    } catch (err) {
      alert(err);
    } finally {
      setLoading(false);
    }
  };

  const modalProps = (r, type = 'editions') => {
    return {
      title: `SET ${type.toUpperCase()}`,
      onClose: () => {
        resetSel(r, type);
      },
      content: () => (
        <div className={classes.column}>
          {get(props, [type, 'data'], [])
            .filter((d) => get(d, 'ownerRealmId') == myRealmId)
            .map((e, i) => {
              const key = [r.id, type, e.id];
              const isActive = type === 'scorecards' ? e.active != 0 : true;
              return (
                <FormControlLabel
                  control={<Checkbox checked={!!get(selItems, key)} onChange={() => handleChange(r, e, type)} />}
                  label={e.name + (type === 'scorecards' ? (isActive ? ` (active)` : '') : '')}
                />
              );
            })}
        </div>
      ),
    };
  };

  const renderItem = (p, i, styles, r, type = 'scorecard') => {
    const isEq = (() => {
      const { clientId, vendorId } = r;
      const { ownerRealmId } = p;

      if (clientId == myRealmId) {
        return ownerRealmId == vendorId;
      } else if (vendorId == myRealmId) {
        return ownerRealmId == clientId;
      }
      return false;
    })();

    return (
      <div key={i} className={clsx(styles.titleRow, styles.title, isEq && classes.italic)}>
        <div>{type === 'scorecard' ? <Icon>assignment</Icon> : <Icon>book</Icon>}</div>
        <div className={styles.realmText}>{get(p, 'name', '')}</div>
      </div>
    );
  };

  const permItems = ['createTasks', 'createReports', 'createLibrary'];

  return (
    <div style={props.style} className={classes.realmList}>
      <Typography variant="h5">{title}</Typography>

      <div className={clsx('vendorsInnerContainer', classes.innerContainer)}>
        {!loading && !!size(data) && canEdit && (
          <Button className={classes.editButton} color={isEdit ? 'secondary' : 'primary'} variant="contained" startIcon={<Icon>{isEdit ? 'close' : 'edit'}</Icon>} onClick={toggleEdit}>
            Edit
          </Button>
        )}

        {loading && (
          <div className={styles.centerColumn}>
            <Loader />
          </div>
        )}

        {!loading && !size(data) && <Typography variant="subtitle1">None</Typography>}

        {!loading && !!size(data) && (
          <div className={clsx('vendorRealmContainer', classes.realmContainer, hideBorders && classes.hideBorders)}>
            {data
              .sort((a, b) => sortRealmNames(a, b, me))
              .map((realm) => {
                const userPerms = getUserAppPermissions(realm.permissions);
                const perms = isEdit ? filterPermissionsByNav().filter((f) => permItems.find((pi) => f.value === pi)) : userPerms;
                return (
                  <div key={realm.id} className={clsx(classes.realm, hideBorders && classes.hideBorders)}>
                    {isEdit && (
                      <Button className={classes.delete} onClick={() => onDelete(realm)}>
                        DELETE
                      </Button>
                    )}
                    <Typography variant="subtitle1">{getRealmName(realm)}</Typography>
                    <FormGroup size="small">
                      {(perms || []).map((perm) => {
                        return (
                          <FormControlLabel
                            key={perm.value}
                            control={
                              <Checkbox
                                size="small"
                                disabled={!isEdit || nameType !== 'vendor'}
                                checked={isEdit ? !!get(state, getSelector(realm, perm)) : (userPerms || []).find((up) => up.value === perm.value)}
                                onChange={() => checkChange(realm, perm)}
                                name={perm.value}
                              />
                            }
                            label={perm.title}
                          />
                        );
                      })}
                    </FormGroup>
                    <Droprealms realms={get(realm, 'realms', [])} onUpdate={(selRealms) => updateRealms(realm, selRealms)} showModify={isEdit && nameType === 'vendor'} />
                    <Dropitems
                      items={get(realm, 'editions', [])}
                      renderItem={(p, i, s) => renderItem(p, i, s, realm, 'edition')}
                      modalProps={modalProps(realm, 'editions')}
                      onUpdate={() => updateItems(realm, 'editions')}
                      type="Edition"
                      icon={<Icon>book</Icon>}
                      showModify={isEdit}
                    />
                    <Dropitems
                      items={get(realm, 'scorecards', [])}
                      renderItem={(p, i, s) => renderItem(p, i, s, realm)}
                      modalProps={modalProps(realm, 'scorecards')}
                      icon={<Icon>assignment</Icon>}
                      onUpdate={() => updateItems(realm, 'scorecards')}
                      type="Scorecard"
                      showModify={isEdit}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <Confirm {...confirmModal} />
    </div>
  );
};

export default PermissionGate((props) => {
  const classes = useStyles();
  const vendors = useVendors(undefined, { useCache: false });
  const clients = useClients(undefined, undefined, { useCache: false });
  const editions = useRetrieveData({ url: '/editions' }, { build: ['editions'], params: { filter: { status: 'archived,active', edition_type: 'course,document' } } });
  const scorecards = useRetrieveData({ url: '/scorecards' }, { build: ['scorecards'], params: { filter: { withInactive: 1 } } });

  return (
    <div className={classes.container}>
      <VendorList editions={editions} scorecards={scorecards} {...vendors} nameType={'vendor'} style={{ marginVertical: 10 }} title="Vendors" />
      <VendorList editions={editions} scorecards={scorecards} {...clients} title="Clients" />
    </div>
  );
});
